import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Heart, Mic, Square, Play, Pause, Trash2, Send, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* Mini voice recorder (self-contained — no auth required, no main app context) */
function MiniRecorder({ onChange }) {
  const [state, setState] = useState("idle"); // idle | recording | preview
  const [seconds, setSeconds] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    if (mediaRef.current && mediaRef.current.state === "recording") mediaRef.current.stop();
  }, [previewUrl]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        setPreviewUrl(URL.createObjectURL(blob));
        setState("preview");
        onChange(blob);
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      setState("recording");
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1 >= 90 ? (stop(), s + 1) : s + 1), 1000);
    } catch { toast.error("Couldn't access microphone"); }
  };
  const stop = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaRef.current && mediaRef.current.state === "recording") mediaRef.current.stop();
  };
  const discard = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null); setSeconds(0); setState("idle"); onChange(null);
  };
  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  if (state === "idle") {
    return (
      <button type="button" onClick={start} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium border border-terracotta-500/40 text-terracotta-600 hover:bg-terracotta-500/10" data-testid="enc-record-btn">
        <Mic className="h-4 w-4" /> Add a voice message (optional)
      </button>
    );
  }
  if (state === "recording") {
    return (
      <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium bg-terracotta-500/15 text-terracotta-600 border border-terracotta-500/40" data-testid="enc-recording">
        <span className="h-2 w-2 rounded-full bg-terracotta-500 animate-pulse" />
        Recording {fmt(seconds)}
        <button type="button" onClick={stop} className="ml-1.5 p-1 rounded-full bg-terracotta-500 text-white">
          <Square className="h-3 w-3 fill-white" />
        </button>
      </div>
    );
  }
  return (
    <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm border border-forest-600/30 bg-forest-600/5" data-testid="enc-preview">
      <button type="button" onClick={() => { if (playing) audioRef.current.pause(); else audioRef.current.play(); }} className="p-1 rounded-full bg-forest-600 text-sand-100">
        {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
      </button>
      <audio ref={audioRef} src={previewUrl} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} />
      <span className="text-forest-700 font-medium">{fmt(seconds)}</span>
      <button type="button" onClick={discard} className="p-1 rounded-full text-terracotta-600 hover:bg-terracotta-500/10" data-testid="enc-discard-btn">
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export default function Encourage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState("");
  const [parentName, setParentName] = useState("");
  const [audioBlob, setAudioBlob] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/encourage/${token}`)
      .then((r) => { if (!cancelled) setInfo(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "This link is invalid or has expired."); });
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 2) return toast.error("Write a short note first");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("message", message);
      fd.append("parent_name", parentName);
      if (audioBlob) {
        fd.append("audio", audioBlob, "voice.webm");
      }
      const r = await axios.post(`${API}/encourage/${token}`, fd);
      setSubmitted(true);
      setInfo((p) => p ? { ...p, remaining_uses: r.data.remaining_uses } : p);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send");
    } finally { setSubmitting(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen geo-bg grid place-items-center p-6">
        <div className="max-w-md w-full hifz-card text-center" data-testid="encourage-error">
          <AlertTriangle className="h-10 w-10 mx-auto text-terracotta-500" />
          <h1 className="mt-3 font-display font-semibold text-2xl text-forest-700">Link unavailable</h1>
          <p className="mt-2 text-sm text-foreground/70">{error}</p>
          <p className="mt-3 text-xs text-muted-foreground">A fresh link will arrive in next Friday&apos;s Pulse email, in sha Allah.</p>
        </div>
      </div>
    );
  }
  if (!info) {
    return <div className="min-h-screen geo-bg grid place-items-center text-muted-foreground">Loading…</div>;
  }

  if (submitted) {
    return (
      <div className="min-h-screen geo-bg grid place-items-center p-6">
        <div className="max-w-md w-full hifz-card text-center" data-testid="encourage-success">
          <div className="h-14 w-14 rounded-full bg-gold-500/15 text-gold-600 grid place-items-center mx-auto">
            <Heart className="h-7 w-7 fill-gold-500 text-gold-500" />
          </div>
          <h1 className="mt-4 font-display font-semibold text-2xl text-forest-700">Sent with love</h1>
          <p className="mt-2 text-foreground/75 leading-relaxed">
            {info.student_first_name} will see your message the next time they open the app. Jazak Allah khair for taking the time.
          </p>
          <p className="mt-3 text-xs text-muted-foreground">
            You have {info.remaining_uses} more encouragement{info.remaining_uses === 1 ? "" : "s"} available with this link.
          </p>
          {info.remaining_uses > 0 && (
            <button onClick={() => { setSubmitted(false); setMessage(""); setAudioBlob(null); }} className="mt-5 btn-accent text-sm" data-testid="encourage-send-another">
              Send another
            </button>
          )}
        </div>
      </div>
    );
  }

  const exhausted = info.remaining_uses <= 0;

  return (
    <div className="min-h-screen geo-bg p-6 md:p-10">
      <div className="max-w-2xl mx-auto" data-testid="encourage-page">
        <div className="flex items-center gap-3 mb-6">
          <img src="/Hikma-Verse-Logo.png" alt="Hikmah Verses" className="h-10 w-10 rounded-xl object-cover" />
          <div>
            <div className="font-display font-semibold text-forest-700">Hifz with Hikmah</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">HikmahVerses</div>
          </div>
        </div>

        <div className="hifz-card">
          <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">A message for {info.student_first_name}</div>
          <h1 className="mt-1 font-display font-semibold text-3xl md:text-4xl text-forest-700">
            Send {info.student_first_name} a word of encouragement
          </h1>
          <p className="mt-3 text-foreground/75 leading-relaxed">
            Children remember parental encouragement long after they forget the praise of teachers. Even one line is enough &mdash; a voice message means even more.
          </p>

          {exhausted ? (
            <div className="mt-6 p-4 rounded-2xl border border-terracotta-500/30 bg-terracotta-500/8 text-sm text-foreground/85" data-testid="encourage-exhausted">
              You&apos;ve used this link the maximum number of times. A fresh link arrives in next Friday&apos;s Pulse email, in sha Allah.
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Your name (optional)</label>
                <input
                  value={parentName} onChange={(e) => setParentName(e.target.value)}
                  placeholder="e.g. Abu Aisha"
                  className="mt-1 w-full rounded-xl border border-sand-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-forest-600/40 focus:border-forest-600"
                  data-testid="encourage-name"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Your message</label>
                <textarea
                  required value={message} onChange={(e) => setMessage(e.target.value)}
                  maxLength={1000} rows={5}
                  placeholder={`Mashallah ${info.student_first_name}, I am proud of you for…`}
                  className="mt-1 w-full rounded-xl border border-sand-300 bg-white px-4 py-3 text-base leading-relaxed focus:outline-none focus:ring-2 focus:ring-forest-600/40 focus:border-forest-600"
                  data-testid="encourage-message"
                />
                <div className="text-right text-[11px] text-muted-foreground mt-1">{message.length}/1000</div>
              </div>
              <div>
                <MiniRecorder onChange={setAudioBlob} />
              </div>
              <div className="flex items-center justify-between gap-3 pt-2 flex-wrap">
                <div className="text-xs text-muted-foreground">
                  {info.remaining_uses} encouragement{info.remaining_uses === 1 ? "" : "s"} remaining with this link
                </div>
                <button type="submit" disabled={submitting || message.trim().length < 2} className="btn-primary disabled:opacity-50" data-testid="encourage-submit">
                  <Send className="h-4 w-4" /> {submitting ? "Sending…" : `Send to ${info.student_first_name}`}
                </button>
              </div>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Sent only to {info.student_first_name}&rsquo;s HikmahVerses account &mdash; not shared with anyone else.
        </p>
      </div>
    </div>
  );
}
